import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'constants/AppRoutes';
import { COMPANY_MAP_TABS } from 'constants/interfaces';
import { useAiScreenActions } from 'contexts/AiScreenActionsContext';
import { useAppDispatch } from 'store/hooks/useApp';
import { setValueChainMapState } from 'store/slices/valueChainMap/slice';

import type { IOpenImpactAnalysisAction } from 'utils/interfaces/IAiScreenAction';

/** What the caller says: which analysis, and whether to press start. The type is implied. */
export type TOpenImpactAnalysis = Omit<IOpenImpactAnalysisAction, 'type'>;

/**
 * Opens the Impact Scenarios panel of a map on one analysis, from wherever the user is.
 *
 * The map's value chain tab answers the action when it is mounted. When it is not — the user is
 * on another map, another tab, or no map at all — the action waits, and the app navigates to the
 * map's value chain tab, which takes the action as it mounts.
 */
const useOpenImpactAnalysis = (): (request: TOpenImpactAnalysis) => void => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { dispatch: dispatchScreenAction } = useAiScreenActions();

  return useCallback((request: TOpenImpactAnalysis) => {
    if (dispatchScreenAction({ type: 'openImpactAnalysis', ...request })) {
      return;
    }

    dispatch(setValueChainMapState({ currentCompanyMapTab: COMPANY_MAP_TABS.VALUE_CHAIN_MAP }));
    navigate(AppRoutes.companyMap.replace(':valueChainMapId', `${request.valueChainMapId}`));
  }, [dispatch, dispatchScreenAction, navigate]);
};

export default useOpenImpactAnalysis;
